import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Zap, Plus, Trash2, Pencil, Moon, Save } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layout/PageHeader";
import { api, errMsg } from "@/lib/api";
import { TemplatesSection } from "@/components/automation/TemplatesSection";
import { CsatSection } from "@/components/automation/CsatSection";

const EMPTY = { shortcut: "", title: "", text: "" };
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const QuickReplies = () => {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["quick-replies"], queryFn: () => api.get("/quick-replies").then((r) => r.data) });
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/quick-replies/${editId}`, form);
      else await api.post("/quick-replies", form);
      toast.success(editId ? "Risposta aggiornata" : "Risposta rapida creata");
      setForm(EMPTY); setEditId(null);
      qc.invalidateQueries({ queryKey: ["quick-replies"] });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => {
    await api.delete(`/quick-replies/${id}`);
    qc.invalidateQueries({ queryKey: ["quick-replies"] });
    toast.success("Risposta eliminata");
  };
  return (
    <section className="bg-white border border-slate-200 rounded-xl" data-testid="quick-replies-section">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <span className="w-9 h-9 rounded-lg bg-[#DCFCE7] text-[#15803D] grid place-items-center"><Zap size={17} /></span>
        <div><h3 className="font-semibold text-slate-900">Risposte predefinite</h3><p className="text-xs text-slate-500">Gli agenti le richiamano digitando <code className="bg-slate-100 px-1 rounded">/</code> nella chat.</p></div>
      </div>
      <form onSubmit={submit} className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3 border-b border-slate-100">
        <Input data-testid="qr-shortcut-input" required placeholder="/scorciatoia" value={form.shortcut} onChange={(e) => setForm({ ...form, shortcut: e.target.value })} />
        <Input data-testid="qr-title-input" required placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <Textarea data-testid="qr-text-input" required className="col-span-full" rows={3} placeholder="Testo del messaggio…" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
        <div className="col-span-full flex gap-2 justify-end">
          {editId && <Button type="button" variant="ghost" onClick={() => { setEditId(null); setForm(EMPTY); }}>Annulla</Button>}
          <Button data-testid="qr-save-button" type="submit" className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus size={15} className="mr-1.5" />{editId ? "Aggiorna" : "Aggiungi risposta"}</Button>
        </div>
      </form>
      <ul className="divide-y divide-slate-100">
        {items.map((r) => (
          <li key={r.id} data-testid={`qr-item-${r.shortcut}`} className="px-6 py-3.5 flex items-start gap-4 group">
            <code className="text-xs font-semibold text-[#15803D] bg-[#DCFCE7] rounded px-2 py-1 shrink-0">/{r.shortcut}</code>
            <div className="min-w-0 flex-1"><div className="text-sm font-semibold text-slate-800">{r.title}</div><div className="text-sm text-slate-500">{r.text}</div></div>
            <div className="flex gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
              <Button data-testid={`qr-edit-${r.shortcut}`} variant="ghost" size="icon" onClick={() => { setEditId(r.id); setForm({ shortcut: r.shortcut, title: r.title, text: r.text }); }}><Pencil size={15} /></Button>
              <Button data-testid={`qr-delete-${r.shortcut}`} variant="ghost" size="icon" onClick={() => remove(r.id)}><Trash2 size={15} className="text-red-500" /></Button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};

const AwayMessage = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: () => api.get("/settings").then((r) => r.data) });
  const [f, setF] = useState(null);
  useEffect(() => { if (data && !f) setF(data); }, [data, f]);
  if (!f) return null;
  const save = async (patch = {}) => {
    try {
      const body = { away_enabled: f.away_enabled, away_message: f.away_message, work_start: f.work_start, work_end: f.work_end, work_days: f.work_days, timezone: f.timezone, ...patch };
      const { data: s } = await api.put("/settings", body);
      setF(s); qc.setQueryData(["settings"], s);
      toast.success("Messaggio di assenza salvato");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleDay = (i) => setF({ ...f, work_days: f.work_days.includes(i) ? f.work_days.filter((d) => d !== i) : [...f.work_days, i].sort() });
  return (
    <section className="bg-white border border-slate-200 rounded-xl" data-testid="away-message-section">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
        <span className="w-9 h-9 rounded-lg bg-slate-100 text-slate-700 grid place-items-center"><Moon size={17} /></span>
        <div><h3 className="font-semibold text-slate-900">Messaggio di assenza</h3><p className="text-xs text-slate-500">Inviato automaticamente fuori dagli orari lavorativi.</p></div>
        <div className="ml-auto flex items-center gap-2">
          <span data-testid="away-status-label" className={`text-xs font-semibold ${f.away_enabled ? "text-[#15803D]" : "text-slate-400"}`}>{f.away_enabled ? "Attivo" : "Disattivato"}</span>
          <Switch data-testid="away-toggle-switch" checked={f.away_enabled} onCheckedChange={(v) => { setF({ ...f, away_enabled: v }); save({ away_enabled: v }); }} className="data-[state=checked]:bg-[#25D366]" />
        </div>
      </div>
      <div className="p-6 space-y-4">
        <div className="space-y-1.5">
          <Label>Testo del messaggio</Label>
          <Textarea data-testid="away-message-textarea" rows={4} value={f.away_message} onChange={(e) => setF({ ...f, away_message: e.target.value })} />
          <p className="text-[11px] text-slate-400">Usa <code>{"{azienda}"}</code> per inserire il nome dell'azienda.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label>Apertura</Label><Input data-testid="away-work-start-input" type="time" value={f.work_start} onChange={(e) => setF({ ...f, work_start: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Chiusura</Label><Input data-testid="away-work-end-input" type="time" value={f.work_end} onChange={(e) => setF({ ...f, work_end: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Fuso orario</Label><Input data-testid="away-timezone-input" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} /></div>
        </div>
        <div className="space-y-1.5">
          <Label>Giorni lavorativi</Label>
          <div className="flex flex-wrap gap-1.5">
            {DAYS.map((d, i) => (
              <button key={d} type="button" data-testid={`away-day-${i}`} onClick={() => toggleDay(i)} className={`w-12 h-9 rounded-md text-xs font-semibold border transition-colors ${f.work_days.includes(i) ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{d}</button>
            ))}
          </div>
        </div>
        <div className="flex justify-end"><Button data-testid="away-save-button" onClick={() => save()} className="bg-[#0F172A] hover:bg-[#1E293B]"><Save size={15} className="mr-1.5" />Salva</Button></div>
      </div>
    </section>
  );
};

export default function Automation() {
  return (
    <div className="h-full flex flex-col">
      <PageHeader title="Automazione" subtitle="Risposte rapide e messaggi automatici" icon={Zap} />
      <div className="flex-1 overflow-y-auto thin-scroll p-4 md:p-8">
        <div className="max-w-6xl grid xl:grid-cols-2 gap-6 items-start">
          <QuickReplies />
          <AwayMessage />
          <CsatSection />
          <TemplatesSection />
        </div>
      </div>
    </div>
  );
}
